import React, { useId } from 'react';
import { clsx } from 'clsx';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'children'> {
  /** Label text above the select */
  label?: string;
  /** Helper/description text below the label */
  description?: string;
  /** Error message */
  error?: string;
  /** Flat list of options — usar esta prop, NÃO children `<option>` */
  options?: SelectOption[];
  /** Grouped options */
  groups?: SelectOptionGroup[];
  /** Selected value */
  value?: string;
  /** Change handler returning the selected value */
  onChange?: (value: string) => void;
  /** Placeholder option text */
  placeholder?: string;
  /** Full width */
  fullWidth?: boolean;
  /**
   * @deprecated Select não renderiza children. Use a prop `options` ou `groups`.
   * Tipado como `never` para gerar erro de compilação se alguém tentar passar `<option>`.
   */
  children?: never;
}

/**
 * Shared Select component with consistent styling and theme support.
 *
 * Supports flat options, grouped options, placeholder, label, description, and error states.
 *
 * @example
 * ```tsx
 * <Select
 *   label="Status"
 *   options={[
 *     { value: 'active', label: 'Ativo' },
 *     { value: 'inactive', label: 'Inativo' },
 *   ]}
 *   value={status}
 *   onChange={setStatus}
 *   placeholder="Selecione um status"
 * />
 * ```
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      description,
      error,
      options,
      groups,
      value,
      onChange,
      placeholder,
      fullWidth = true,
      className,
      style,
      id: propId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = propId || generatedId;
    const hasError = !!error;

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e.target.value);
    };

    return (
      <div className={clsx({ 'w-full': fullWidth })}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[13px] font-semibold mb-1.5"
            style={{ color: 'var(--theme-text)' }}
          >
            {label}
            {props.required && (
              <span className="ml-1" style={{ color: 'var(--theme-error-color)' }}>*</span>
            )}
          </label>
        )}
        {description && !error && (
          <p className="text-xs mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
            {description}
          </p>
        )}

        <select
          ref={ref}
          id={inputId}
          value={value}
          onChange={handleChange}
          className={clsx(
            'w-full h-[38px] rounded-[9px] border px-[11px] py-1.5 text-sm appearance-none',
            'focus-visible:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          style={{
            backgroundColor: 'var(--theme-input-background)',
            borderColor: hasError ? 'var(--theme-error-color)' : 'var(--theme-input-border)',
            color: value ? 'var(--theme-input-text)' : 'var(--theme-muted-text)',
            transition: 'border-color 150ms, box-shadow 150ms',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 0.75rem center',
            backgroundSize: '1rem',
            paddingRight: '2.5rem',
            ...style,
          }}
          onFocus={(e) => {
            if (!hasError && !props.disabled) {
              e.currentTarget.style.borderColor = 'var(--theme-input-focus)';
              e.currentTarget.style.boxShadow = '0 0 0 3px var(--ring-color)';
            }
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = hasError
              ? 'var(--theme-error-color)'
              : 'var(--theme-input-border)';
            e.currentTarget.style.boxShadow = 'none';
            props.onBlur?.(e);
          }}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
          {groups?.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                  {opt.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {error && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
